/**
 * PermissionsList component
 *
 * Displays a list of permissions for an asset
 * Allows user to update permissions
 */

import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import useLabels from '~hooks/useLabels';
import { updateMamAssetsPermissions } from '~services/api/calls/mam-assets';
import { useMamPermissions } from '~services/api/hooks/mam-assets.hooks';
import { ModalActions, StatusActions } from '~store/actions';
import styles from './PermissionsList.module.css';

type PermissionsListProps = {
  assetId: string;
};
type PermissionsType = {
  view: [];
  download: [];
};

const PermissionsList = ({ assetId }: PermissionsListProps) => {
  // Get permissions data from API hook
  const { data: mamPermissions } = useMamPermissions(Number(assetId));

  // Local state for permissions
  const [permissions, setPermissions] = useState({ view: [], download: [] });

  // Redux dispatch
  const dispatch = useDispatch();

  // Get translated labels
  const { btnAssetPermissionsCancel, btnAssetPermissionsSave } = useLabels([
    'btnAssetPermissionsCancel',
    'btnAssetPermissionsSave',
  ]);

  // Update local state when permissions data changes
  useEffect(() => {
    if (mamPermissions) {
      setPermissions(mamPermissions as PermissionsType);
    }
  }, [mamPermissions]);

  // Helper to update a permission's active status
  const updatePermissionActiveStatus = (category, permissionLabel, isActive) => {
    const permissionExists = permissions[category].some(permission => permission.label === permissionLabel);

    if (permissionExists) {
      setPermissions(prevPermissions => ({
        ...prevPermissions,
        [category]: prevPermissions[category].map(permission =>
          permission.label === permissionLabel ? { ...permission, active: isActive } : permission,
        ),
      }));
    } else {
      const newPermission = mamPermissions[category].find(permission => permission.label === permissionLabel);
      if (newPermission) {
        setPermissions(prevPermissions => ({
          ...prevPermissions,
          [category]: [...prevPermissions[category], { ...newPermission, active: isActive }],
        }));
      }
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (event, category, label) => {
    const { checked: isActive } = event.target;
    updatePermissionActiveStatus(category, label, isActive);
  };

  // Handle save button click
  const handleOnSave = async () => {
    try {
      const response = await updateMamAssetsPermissions(Number(assetId), permissions);
      if (response.view || response.download) {
        dispatch(StatusActions.setSuccessNotification('Permissions successfully saved!'));
      }
    } catch (error) {
      dispatch(StatusActions.setErrorNotification((error as Error).message));
    }
  };

  return (
    <div className="container py-1 px-3">
      {/* View permissions */}
      <div className={styles['permissions-content']}>
        <div className={styles['permissions-section']}>
          <div className={styles['permissions-label']}>
            <label className="font-bold">View Permissions:</label>
          </div>
          <div className="flex flex-wrap flex-row">
            {permissions &&
              permissions?.view.map(permission => (
                <div key={permission.id} className={styles['permissions-input-container']}>
                  <input
                    type="checkbox"
                    name={permission.label}
                    id={permission.label}
                    checked={permission.active}
                    onChange={e => handleCheckboxChange(e, 'view', permission.label)}
                  />{' '}
                  <label htmlFor={permission.label}>{permission.label}</label>
                </div>
              ))}
          </div>
        </div>

        {/* Download permissions */}
        <div className={styles['permissions-section']}>
          <div className={styles['permissions-label']}>
            <label className="font-bold">Download Permissions:</label>
          </div>
          <div className="flex flex-wrap flex-row">
            {permissions &&
              permissions?.download.map(permission => (
                <div key={permission.id} className={styles['permissions-input-container']}>
                  <input
                    type="checkbox"
                    name={permission.label}
                    id={permission.label}
                    checked={permission.active}
                    onChange={e => handleCheckboxChange(e, 'download', permission.label)}
                  />{' '}
                  <label htmlFor={permission.label}>{permission.label}</label>
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className={styles['permissions-footer']}>
        <button
          onClick={handleOnSave}
          type="submit"
          name="save"
          className="button button--primary js-form-submit form-submit  btn btn-default c-button c-button--dark"
        >
          {btnAssetPermissionsSave}
        </button>
        <button
          onClick={() => {
            dispatch(ModalActions.hide());
          }}
          type="submit"
          name="cancel"
          className="button button--primary js-form-submit form-submit  btn btn-default c-button ml-4"
        >
          {btnAssetPermissionsCancel}
        </button>
      </div>
    </div>
  );
};

export default PermissionsList;
